// src/services/cmoService.ts

import apiClient from '../api/apiClient';
import { normalizeStrapi, formatRecordForStrapi } from '../utils/strapi-normalizer';
import { StrapiResponse } from '@/types/common';

/**
 * CMO Service - Quản lý tổ chức bản quyền âm nhạc (Collective Management Organizations)
 * Tích hợp với Vietrro Infrastructure Architecture
 */

// ========================================
// INTERFACES & TYPES
// ========================================

export interface ExternalWork {
  id: string;
  documentId?: string;
  title: string;
  originalTitle?: string;
  author: string;
  originalAuthor?: string;
  workType: 'music' | 'video' | 'image' | 'text' | 'software' | 'other';
  territory: string;
  originalTerritory?: string;
  cmoOrganization?: string;
  registrationNumber?: string;
  originalRegistrationNumber?: string;
  isVerified: boolean;
  verificationDate?: string;
  metadata?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface CMOOrganization {
  id: string;
  documentId?: string;
  name: string;
  code: string;
  country: string;
  website?: string;
  contactEmail: string;
  contactPhone?: string;
  apiEndpoint?: string;
  apiKey?: string;
  supportedWorkTypes: string[];
  processingTimeEstimate: number; // days
  averageFeeRange: {
    min: number;
    max: number;
    currency: string;
  };
  isActive: boolean;
  hasAPI: boolean;
  requiresManualSubmission: boolean;
  integrationNotes?: string;
  lastSyncAt?: string;
}

export interface ExternalWorkSearchRequest {
  query: string;
  workType?: string;
  territory?: string;
  author?: string;
  registrationNumber?: string;
  page?: number;
  pageSize?: number;
  filters?: {
    verifiedOnly?: boolean;
    dateFrom?: string;
    dateTo?: string;
  };
}

export interface ExternalWorkSearchResponse {
  results: ExternalWork[];
  total: number;
  page: number;
  pageSize: number;
  searchTime: number;
  suggestions?: string[];
  relatedCMOs?: CMOOrganization[];
}

export interface CMORequestPayload {
  workId: string;
  licenseType: string;
  intendedUse: string;
  territory: string;
  duration?: string;
  commercialUse: boolean;
  estimatedAudience?: number;
  distributionChannels?: string[];
  contactInfo: {
    organization: string;
    contactPerson: string;
    email: string;
    phone?: string;
    address?: string;
  };
  additionalInfo?: string;
  urgentRequest?: boolean;
  budget?: {
    min: number;
    max: number;
    currency: string;
  };
}

export interface CMORequestResponse {
  requestId: string;
  cmoReferenceNumber?: string;
  status: 'submitted' | 'pending' | 'processing' | 'approved' | 'rejected' | 'expired';
  estimatedResponseTime?: number;
  message: string;
  nextSteps: string[];
  documents?: string[];
  fees?: {
    amount: number;
    currency: string;
    dueDate?: string;
  };
}

export interface PreLicenseRequest {
  id: string;
  documentId?: string;
  requestNumber: string;
  title: string;
  description: string;
  workType: string;
  originalAuthor?: string;
  originalPublisher?: string;
  estimatedOriginCountry?: string;
  knownCMO?: string;
  externalWorkId?: string;
  status: 'submitted' | 'under_review' | 'legal_processing' | 'cmo_contacted' | 'cmo_negotiating' | 'acquired' | 'ready_for_license' | 'completed' | 'failed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  depositAmount: number;
  estimatedLicenseFee?: number;
  currency: string;
  targetCompletionDate?: string;
  assignedTo?: string;
  intendedUse: string;
  requestedBy: string;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// CMO SERVICE CLASS
// ========================================

class CMOService {
  private readonly baseUrl = '/api';

  // ========================================
  // CMO ORGANIZATIONS MANAGEMENT
  // ========================================

  /**
   * Lấy tất cả CMO organizations
   */
  async getAllCMOs(): Promise<CMOOrganization[]> {
    try {
      console.log('📋 Fetching all CMO organizations');

      const result = await apiClient.searchRecords<CMOOrganization>(
        'cmo-organization',
        { isActive: true },
        { page: 1, pageSize: 100 },
        {
          populate: 'deep',
          sort: 'name:asc'
        }
      );

      if (!result.success) {
        console.warn('⚠️ Failed to fetch CMOs from API, using fallback');
        return this.getFallbackCMOs();
      }

      console.log(`✅ Found ${result.data?.data.length || 0} CMO organizations`);
      return result.data?.data || this.getFallbackCMOs();
    } catch (error) {
      console.error('❌ Error fetching CMOs:', error);
      return this.getFallbackCMOs();
    }
  }

  /**
   * Lấy CMO theo code
   */
  async getCMOByCode(code: string): Promise<CMOOrganization | null> {
    try {
      console.log(`🔍 Fetching CMO by code: ${code}`);

      const result = await apiClient.searchRecords<CMOOrganization>(
        'cmo-organization',
        { code: code.toUpperCase() },
        { page: 1, pageSize: 1 },
        { populate: 'deep' }
      );

      if (!result.success || !result.data?.data.length) {
        console.warn(`⚠️ CMO not found for code: ${code}`);
        return null;
      }

      console.log(`✅ Found CMO: ${result.data.data[0].name}`);
      return result.data.data[0];
    } catch (error) {
      console.error('❌ Error fetching CMO by code:', error);
      return null;
    }
  }

  /**
   * Lấy CMO theo quốc gia
   */
  async getCMOsByCountry(country: string): Promise<CMOOrganization[]> {
    try {
      console.log(`🌍 Fetching CMOs for country: ${country}`);

      const result = await apiClient.searchRecords<CMOOrganization>(
        'cmo-organization',
        {
          country: country.toUpperCase(),
          isActive: true
        },
        { page: 1, pageSize: 50 },
        { populate: 'deep' }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log(`✅ Found ${result.data?.data.length || 0} CMOs for ${country}`);
      return result.data?.data || [];
    } catch (error) {
      console.error('❌ Error fetching CMOs by country:', error);
      return [];
    }
  }

  /**
   * Tạo CMO organization mới
   */
  async createCMO(cmoData: Partial<CMOOrganization>): Promise<CMOOrganization> {
    try {
      console.log(`➕ Creating new CMO: ${cmoData.name}`);

      const result = await apiClient.createRecord<CMOOrganization>(
        'cmo-organization',
        {
          ...cmoData,
          code: cmoData.code?.toUpperCase(),
          isActive: true,
          hasAPI: Boolean(cmoData.apiEndpoint),
          requiresManualSubmission: !cmoData.apiEndpoint
          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ CMO created successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to create CMO:', error);
      throw new Error('Không thể tạo CMO organization. Vui lòng thử lại sau.');
    }
  }

  // ========================================
  // EXTERNAL WORK SEARCH
  // ========================================

  /**
   * Tìm kiếm external works
   */
  async searchExternalWorks(request: ExternalWorkSearchRequest): Promise<ExternalWorkSearchResponse> {
    try {
      console.log(`🔍 Searching external works: "${request.query}"`);

      const startTime = Date.now();

      // Primary search in Vietrro's external works database
      const vietrroResults = await this.searchVietrroExternalWorks(request);

      // If not enough results, search external CMO APIs
      const externalResults = vietrroResults.results.length < 5
        ? await this.searchExternalCMOAPIs(request)
        : [];

      const combinedResults = [...vietrroResults.results, ...externalResults];

      // Remove duplicates
      const uniqueResults = this.deduplicateWorks(combinedResults);

      // Get related CMOs for suggestions
      const relatedCMOs = await this.getRelatedCMOs(request.query, request.territory);

      const searchTime = Date.now() - startTime;

      const response: ExternalWorkSearchResponse = {
        results: uniqueResults.slice(0, request.pageSize || 20),
        total: uniqueResults.length,
        page: request.page || 1,
        pageSize: request.pageSize || 20,
        searchTime,
        suggestions: this.generateSearchSuggestions(request.query),
        relatedCMOs
      };

      console.log(`✅ Search completed in ${searchTime}ms: ${response.results.length} results`);
      return response;
    } catch (error) {
      console.error('❌ Error searching external works:', error);
      return {
        results: [],
        total: 0,
        page: request.page || 1,
        pageSize: request.pageSize || 20,
        searchTime: Date.now(),
        suggestions: [],
        relatedCMOs: []
      };
    }
  }

  /**
   * Tìm kiếm trong database Vietrro
   */
  private async searchVietrroExternalWorks(request: ExternalWorkSearchRequest): Promise<ExternalWorkSearchResponse> {
    try {
      const filters: Record<string, any> = {};

      if (request.query) {
        filters['$or'] = [
          { 'title[$containsi]': request.query },
          { 'originalTitle[$containsi]': request.query },
          { 'author[$containsi]': request.query },
          { 'originalAuthor[$containsi]': request.query }
        ];
      }

      if (request.workType) {
        filters['workType[$eq]'] = request.workType;
      }

      if (request.territory) {
        filters['$or'] = [
          ...(filters['$or'] || []),
          { 'territory[$eq]': request.territory },
          { 'originalTerritory[$eq]': request.territory }
        ];
      }

      if (request.author) {
        filters['$or'] = [
          ...(filters['$or'] || []),
          { 'author[$containsi]': request.author },
          { 'originalAuthor[$containsi]': request.author }
        ];
      }

      if (request.registrationNumber) {
        filters['$or'] = [
          ...(filters['$or'] || []),
          { 'registrationNumber[$eq]': request.registrationNumber },
          { 'originalRegistrationNumber[$eq]': request.registrationNumber }
        ];
      }

      if (request.filters?.verifiedOnly) {
        filters['isVerified[$eq]'] = true;
      }

      if (request.filters?.dateFrom) {
        filters['createdAt[$gte]'] = request.filters.dateFrom;
      }

      if (request.filters?.dateTo) {
        filters['createdAt[$lte]'] = request.filters.dateTo;
      }

      const result = await apiClient.searchRecords<ExternalWork>(
        'external-work',
        filters,
        {
          page: request.page || 1,
          pageSize: request.pageSize || 20
        },
        {
          populate: 'deep',
          sort: 'updatedAt:desc'
        }
      );

      if (!result.success) {
        throw result.error;
      }

      return {
        results: result.data?.data || [],
        total: result.data?.meta?.pagination?.total || 0,
        page: result.data?.meta?.pagination?.page || 1,
        pageSize: result.data?.meta?.pagination?.pageSize || 20,
        searchTime: Date.now()
      };
    } catch (error) {
      console.error('❌ Error searching Vietrro external works:', error);
      return {
        results: [],
        total: 0,
        page: 1,
        pageSize: 20,
        searchTime: Date.now()
      };
    }
  }

  /**
   * Tìm kiếm trên các CMO APIs
   */
  private async searchExternalCMOAPIs(request: ExternalWorkSearchRequest): Promise<ExternalWork[]> {
    const results: ExternalWork[] = [];

    try {
      console.log('🌐 Searching external CMO APIs...');

      // Search multiple CMOs in parallel
      const searchPromises = [
        this.searchASCAP(request).catch(() => []),
        this.searchBMI(request).catch(() => []),
        this.searchGEMA(request).catch(() => []),
        this.searchSOCAN(request).catch(() => []),
        this.searchSESAC(request).catch(() => [])
      ];

      const searchResults = await Promise.all(searchPromises);
      searchResults.forEach(apiResults => {
        results.push(...apiResults);
      });

      console.log(`✅ External CMO search completed: ${results.length} results`);
    } catch (error) {
      console.error('❌ Error searching external CMO APIs:', error);
    }

    return results;
  }

  /**
   * Search ASCAP database
   */
  private async searchASCAP(request: ExternalWorkSearchRequest): Promise<ExternalWork[]> {
    try {
      // Mock ASCAP API integration
      // In real implementation, integrate with ASCAP's actual API
      if (!request.query || request.query.length < 3) {
        return [];
      }

      const mockResults: ExternalWork[] = [
        {
          id: `ascap_${Date.now()}_1`,
          title: `${request.query} (ASCAP Work)`,
          author: 'ASCAP Artist',
          workType: 'music',
          territory: 'US',
          cmoOrganization: 'ASCAP',
          registrationNumber: `ASCAP-${Date.now()}`,
          isVerified: true,
          metadata: {
            language: 'en',
            source: 'ASCAP API',
            searchRelevance: 0.85
          },
          
        }
      ];

      return mockResults;
    } catch (error) {
      console.error('❌ Error searching ASCAP:', error);
      return [];
    }
  }

  /**
   * Search BMI database
   */
  private async searchBMI(request: ExternalWorkSearchRequest): Promise<ExternalWork[]> {
    try {
      // Mock BMI API integration
      if (!request.query || request.territory && request.territory !== 'US') {
        return [];
      }

      const mockResults: ExternalWork[] = [
        {
          id: `bmi_${Date.now()}_1`,
          title: `${request.query} (BMI Work)`,
          author: 'BMI Artist',
          workType: 'music',
          territory: 'US',
          cmoOrganization: 'BMI',
          registrationNumber: `BMI-${Date.now()}`,
          isVerified: true,
          metadata: {
            language: 'en',
            source: 'BMI API',
            searchRelevance: 0.80
          },
          
        }
      ];

      return mockResults;
    } catch (error) {
      console.error('❌ Error searching BMI:', error);
      return [];
    }
  }

  /**
   * Search GEMA database
   */
  private async searchGEMA(request: ExternalWorkSearchRequest): Promise<ExternalWork[]> {
    try {
      // Mock GEMA API integration
      if (!request.query || request.territory && request.territory !== 'DE') {
        return [];
      }

      const mockResults: ExternalWork[] = [
        {
          id: `gema_${Date.now()}_1`,
          title: `${request.query} (GEMA Work)`,
          author: 'GEMA Künstler',
          workType: 'music',
          territory: 'DE',
          cmoOrganization: 'GEMA',
          registrationNumber: `GEMA-${Date.now()}`,
          isVerified: true,
          metadata: {
            language: 'de',
            source: 'GEMA API',
            searchRelevance: 0.75
          },
          
        }
      ];

      return mockResults;
    } catch (error) {
      console.error('❌ Error searching GEMA:', error);
      return [];
    }
  }

  /**
   * Search SOCAN database
   */
  private async searchSOCAN(request: ExternalWorkSearchRequest): Promise<ExternalWork[]> {
    try {
      // Mock SOCAN API integration
      if (!request.query || request.territory && request.territory !== 'CA') {
        return [];
      }

      const mockResults: ExternalWork[] = [
        {
          id: `socan_${Date.now()}_1`,
          title: `${request.query} (SOCAN Work)`,
          author: 'SOCAN Artist',
          workType: 'music',
          territory: 'CA',
          cmoOrganization: 'SOCAN',
          registrationNumber: `SOCAN-${Date.now()}`,
          isVerified: true,
          metadata: {
            language: 'en',
            source: 'SOCAN API',
            searchRelevance: 0.70
          },
          
        }
      ];

      return mockResults;
    } catch (error) {
      console.error('❌ Error searching SOCAN:', error);
      return [];
    }
  }

  /**
   * Search SESAC database
   */
  private async searchSESAC(request: ExternalWorkSearchRequest): Promise<ExternalWork[]> {
    try {
      // Mock SESAC API integration
      if (!request.query || request.territory && request.territory !== 'US') {
        return [];
      }

      const mockResults: ExternalWork[] = [
        {
          id: `sesac_${Date.now()}_1`,
          title: `${request.query} (SESAC Work)`,
          author: 'SESAC Artist',
          workType: 'music',
          territory: 'US',
          cmoOrganization: 'SESAC',
          registrationNumber: `SESAC-${Date.now()}`,
          isVerified: true,
          metadata: {
            language: 'en',
            source: 'SESAC API',
            searchRelevance: 0.65
          },
          
        }
      ];

      return mockResults;
    } catch (error) {
      console.error('❌ Error searching SESAC:', error);
      return [];
    }
  }

  // ========================================
  // CMO LICENSE REQUEST MANAGEMENT
  // ========================================

  /**
   * Gửi yêu cầu license đến CMO
   */
  async submitCMORequest(payload: CMORequestPayload): Promise<CMORequestResponse> {
    try {
      console.log(`📤 Submitting CMO request for work: ${payload.workId}`);

      // First, get the external work details
      const work = await this.getExternalWorkById(payload.workId);
      if (!work) {
        throw new Error('Work not found');
      }

      // Identify the appropriate CMO
      const cmo = work.cmoOrganization
        ? await this.getCMOByCode(work.cmoOrganization)
        : await this.identifyAppropriateCMO(work);

      if (!cmo) {
        throw new Error('Unable to identify appropriate CMO for this work');
      }

      // Submit request based on CMO's integration method
      let response: CMORequestResponse;

      if (cmo.hasAPI && cmo.apiEndpoint) {
        response = await this.submitAPIRequest(cmo, payload, work);
      } else {
        response = await this.submitEmailRequest(cmo, payload, work);
      }

      // Log the request for tracking
      await this.logCMORequest(cmo, payload, response);

      console.log(`✅ CMO request submitted: ${response.requestId}`);
      return response;
    } catch (error) {
      console.error('❌ Failed to submit CMO request:', error);
      throw new Error(`Không thể gửi yêu cầu đến CMO: ${error.message}`);
    }
  }

  /**
   * Gửi request qua API
   */
  private async submitAPIRequest(
    cmo: CMOOrganization,
    payload: CMORequestPayload,
    work: ExternalWork
  ): Promise<CMORequestResponse> {
    try {
      console.log(`🔗 Submitting API request to ${cmo.name}`);

      // Mock API request - in production, implement actual CMO API calls
      const requestId = `API_${cmo.code}_${Date.now()}`;

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      const response: CMORequestResponse = {
        requestId,
        cmoReferenceNumber: `${cmo.code}-REF-${Date.now()}`,
        status: 'submitted',
        estimatedResponseTime: cmo.processingTimeEstimate,
        message: `Request submitted successfully to ${cmo.name} via API`,
        nextSteps: [
          'Request has been submitted to CMO API',
          'CMO will review your request automatically',
          'You will receive updates via email',
          `Processing typically takes ${cmo.processingTimeEstimate} business days`
        ],
        fees: {
          amount: this.estimateLicenseFee(payload, cmo),
          currency: cmo.averageFeeRange.currency,
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        }
      };

      return response;
    } catch (error) {
      console.error('❌ API request failed:', error);
      throw new Error(`API request to ${cmo.name} failed: ${error.message}`);
    }
  }

  /**
   * Gửi request qua email
   */
  private async submitEmailRequest(
    cmo: CMOOrganization,
    payload: CMORequestPayload,
    work: ExternalWork
  ): Promise<CMORequestResponse> {
    try {
      console.log(`📧 Submitting email request to ${cmo.name}`);

      // Generate email content
      const emailContent = this.generateCMORequestEmail(payload, work, cmo);

      // Send email through Vietrro's email service
      await this.sendCMOEmail(cmo.contactEmail, emailContent, payload);

      const requestId = `EMAIL_${cmo.code}_${Date.now()}`;

      const response: CMORequestResponse = {
        requestId,
        status: 'submitted',
        estimatedResponseTime: cmo.processingTimeEstimate,
        message: `Request submitted successfully to ${cmo.name} via email`,
        nextSteps: [
          `Email sent to ${cmo.contactEmail}`,
          'CMO will review your request manually',
          'You will receive a response via email',
          `Expected response time: ${cmo.processingTimeEstimate} business days`
        ]
      };

      return response;
    } catch (error) {
      console.error('❌ Email request failed:', error);
      throw new Error(`Email request to ${cmo.name} failed: ${error.message}`);
    }
  }

  // ========================================
  // PRE-LICENSE REQUEST MANAGEMENT
  // ========================================

  /**
   * Tạo pre-license request
   */
  async createPreLicenseRequest(requestData: Partial<PreLicenseRequest>): Promise<PreLicenseRequest> {
    try {
      console.log(`➕ Creating pre-license request: ${requestData.title}`);

      const result = await apiClient.createRecord<PreLicenseRequest>(
        'pre-license-request',
        {
          ...requestData,
          requestNumber: this.generateRequestNumber(),
          status: 'submitted',
          priority: requestData.priority || 'medium',
          currency: requestData.currency || 'VND',
          depositAmount: requestData.depositAmount || 100000, // 100k VND default          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log(`✅ Pre-license request created: ${result.data!.requestNumber}`);
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to create pre-license request:', error);
      throw new Error('Không thể tạo yêu cầu pre-license. Vui lòng thử lại sau.');
    }
  }

  /**
   * Cập nhật pre-license request
   */
  async updatePreLicenseRequest(
    requestId: string,
    updateData: Partial<PreLicenseRequest>
  ): Promise<PreLicenseRequest> {
    try {
      console.log(`📝 Updating pre-license request: ${requestId}`);

      const result = await apiClient.updateRecordById<PreLicenseRequest>(
        'pre-license-request',
        requestId,
        {
          ...updateData,
          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ Pre-license request updated successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to update pre-license request:', error);
      throw new Error('Không thể cập nhật yêu cầu pre-license. Vui lòng thử lại sau.');
    }
  }

  /**
   * Lấy pre-license request theo ID
   */
  async getPreLicenseRequest(requestId: string): Promise<PreLicenseRequest | null> {
    try {
      console.log(`🔍 Fetching pre-license request: ${requestId}`);

      const result = await apiClient.fetchRecord<PreLicenseRequest>(
        'pre-license-request',
        requestId,
        { populate: 'deep' }
      );

      if (!result.success) {
        return null;
      }

      console.log('✅ Pre-license request fetched successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to fetch pre-license request:', error);
      return null;
    }
  }

  /**
   * Lấy pre-license requests của user
   */
  async getUserPreLicenseRequests(
    userId: string,
    filters?: Record<string, any>
  ): Promise<PreLicenseRequest[]> {
    try {
      console.log(`📋 Fetching pre-license requests for user: ${userId}`);

      const searchFilters = {
        'requestedBy[documentId][$eq]': userId,
        ...filters
      };

      const result = await apiClient.searchRecords<PreLicenseRequest>(
        'pre-license-request',
        searchFilters,
        { page: 1, pageSize: 100 },
        {
          populate: 'deep',
          sort: 'createdAt:desc'
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log(`✅ Found ${result.data?.data.length || 0} pre-license requests`);
      return result.data?.data || [];
    } catch (error) {
      console.error('❌ Failed to fetch user pre-license requests:', error);
      return [];
    }
  }

  /**
   * Lấy requests cho legal team
   */
  async getLegalTeamRequests(filters?: Record<string, any>): Promise<PreLicenseRequest[]> {
    try {
      console.log('⚖️ Fetching legal team requests');

      const searchFilters = {
        'status[$in]': ['submitted', 'under_review', 'legal_processing', 'cmo_contacted', 'cmo_negotiating'],
        ...filters
      };

      const result = await apiClient.searchRecords<PreLicenseRequest>(
        'pre-license-request',
        searchFilters,
        { page: 1, pageSize: 100 },
        {
          populate: 'deep',
          sort: 'priority:desc,createdAt:asc'
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log(`✅ Found ${result.data?.data.length || 0} legal team requests`);
      return result.data?.data || [];
    } catch (error) {
      console.error('❌ Failed to fetch legal team requests:', error);
      return [];
    }
  }

  // ========================================
  // UTILITY & HELPER METHODS
  // ========================================

  /**
   * Thêm external work
   */
  async addExternalWork(workData: Partial<ExternalWork>): Promise<ExternalWork> {
    try {
      console.log(`➕ Adding external work: ${workData.title}`);

      const result = await apiClient.createRecord<ExternalWork>(
        'external-work',
        {
          ...workData,
          isVerified: workData.isVerified || false          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ External work added successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to add external work:', error);
      throw new Error('Không thể thêm external work. Vui lòng thử lại sau.');
    }
  }

  /**
   * Lấy external work theo ID
   */
  async getExternalWorkById(id: string): Promise<ExternalWork | null> {
    try {
      console.log(`🔍 Fetching external work: ${id}`);

      const result = await apiClient.fetchRecord<ExternalWork>(
        'external-work',
        id,
        { populate: 'deep' }
      );

      if (!result.success) {
        return null;
      }

      console.log('✅ External work fetched successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to fetch external work:', error);
      return null;
    }
  }

  /**
   * Cập nhật external work
   */
  async updateExternalWork(id: string, updateData: Partial<ExternalWork>): Promise<ExternalWork> {
    try {
      console.log(`📝 Updating external work: ${id}`);

      const result = await apiClient.updateRecordById<ExternalWork>(
        'external-work',
        id,
        {
          ...updateData,
          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ External work updated successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to update external work:', error);
      throw new Error('Không thể cập nhật external work. Vui lòng thử lại sau.');
    }
  }

  /**
   * Xác minh external work
   */
  async verifyExternalWork(id: string, verificationNotes?: string): Promise<ExternalWork> {
    try {
      console.log(`✅ Verifying external work: ${id}`);

      const result = await apiClient.updateRecordById<ExternalWork>(
        'external-work',
        id,
        {
          isVerified: true,
          verificationDate: new Date().toISOString(),
          metadata: {
            verificationNotes: verificationNotes || 'Verified by Vietrro team',
            verifiedBy: 'system' // In real app, use actual user ID
          },
          
        }
      );

      if (!result.success) {
        throw result.error;
      }

      console.log('✅ External work verified successfully');
      return result.data!;
    } catch (error) {
      console.error('❌ Failed to verify external work:', error);
      throw new Error('Không thể xác minh external work. Vui lòng thử lại sau.');
    }
  }

  /**
   * Loại bỏ duplicate works
   */
  private deduplicateWorks(works: ExternalWork[]): ExternalWork[] {
    const seen = new Map<string, ExternalWork>();

    works.forEach(work => {
      const key = `${work.title.toLowerCase().trim()}_${work.author.toLowerCase().trim()}_${work.territory}`;

      if (!seen.has(key)) {
        seen.set(key, work);
      } else {
        // If duplicate found, prefer verified works
        const existing = seen.get(key)!;
        if (work.isVerified && !existing.isVerified) {
          seen.set(key, work);
        }
      }
    });

    return Array.from(seen.values());
  }

  /**
   * Tạo search suggestions
   */
  private generateSearchSuggestions(query: string): string[] {
    if (!query || query.length < 2) return [];

    const suggestions: string[] = [];
    const cleanQuery = query.replace(/\s+/g, ' ').trim();

    // Basic suggestions
    suggestions.push(cleanQuery);

    // Add common variations
    if (cleanQuery.length > 3) {
      suggestions.push(`${cleanQuery} original`);
      suggestions.push(`${cleanQuery} cover`);
      suggestions.push(`${cleanQuery} remix`);
      suggestions.push(`${cleanQuery} instrumental`);
    }

    // Remove duplicates and limit
    return [...new Set(suggestions)].slice(0, 5);
  }

  /**
   * Lấy related CMOs
   */
  private async getRelatedCMOs(query: string, territory?: string): Promise<CMOOrganization[]> {
    try {
      const filters: Record<string, any> = { isActive: true };

      if (territory) {
        filters['country'] = territory.toUpperCase();
      }

      const result = await apiClient.searchRecords<CMOOrganization>(
        'cmo-organization',
        filters,
        { page: 1, pageSize: 5 },
        { populate: 'deep' }
      );

      return result.data?.data || [];
    } catch (error) {
      console.error('❌ Error fetching related CMOs:', error);
      return [];
    }
  }

  /**
   * Identify appropriate CMO
   */
  private async identifyAppropriateCMO(work: ExternalWork): Promise<CMOOrganization | null> {
    try {
      console.log(`🎯 Identifying appropriate CMO for work: ${work.title}`);

      const territory = work.originalTerritory || work.territory;
      const workType = work.workType;

      // Get CMOs for the territory
      const territoryCMOs = await this.getCMOsByCountry(territory);

      if (!territoryCMOs.length) {
        console.warn(`⚠️ No CMOs found for territory: ${territory}`);
        return null;
      }

      // Find CMO that supports this work type
      const appropriateCMO = territoryCMOs.find(cmo =>
        cmo.supportedWorkTypes.includes(workType) ||
        cmo.supportedWorkTypes.includes('all')
      );

      if (appropriateCMO) {
        console.log(`✅ Identified CMO: ${appropriateCMO.name}`);
      } else {
        console.warn(`⚠️ No appropriate CMO found for work type: ${workType}`);
      }

      return appropriateCMO || null;
    } catch (error) {
      console.error('❌ Error identifying appropriate CMO:', error);
      return null;
    }
  }

  /**
   * Estimate license fee
   */
  private estimateLicenseFee(payload: CMORequestPayload, cmo: CMOOrganization): number {
    try {
      let baseFee = cmo.averageFeeRange.min;

      // Adjust based on commercial use
      if (payload.commercialUse) {
        baseFee *= 2;
      }

      // Adjust based on audience size
      if (payload.estimatedAudience) {
        if (payload.estimatedAudience > 1000000) {
          baseFee *= 3;
        } else if (payload.estimatedAudience > 100000) {
          baseFee *= 2;
        } else if (payload.estimatedAudience > 10000) {
          baseFee *= 1.5;
        }
      }

      // Adjust based on distribution channels
      if (payload.distributionChannels?.length) {
        baseFee *= (1 + payload.distributionChannels.length * 0.1);
      }

      // Cap at maximum
      const maxFee = cmo.averageFeeRange.max;
      const estimatedFee = Math.min(Math.round(baseFee), maxFee);

      console.log(`💰 Estimated license fee: ${estimatedFee} ${cmo.averageFeeRange.currency}`);
      return estimatedFee;
    } catch (error) {
      console.error('❌ Error estimating license fee:', error);
      return cmo.averageFeeRange.min;
    }
  }

  /**
   * Generate CMO request email
   */
  private generateCMORequestEmail(
    payload: CMORequestPayload,
    work: ExternalWork,
    cmo: CMOOrganization
  ): string {
    const urgentFlag = payload.urgentRequest ? '[URGENT] ' : '';

    return `
Subject: ${urgentFlag}License Request - ${work.title}

Dear ${cmo.name} Team,

Vietrro Platform is writing on behalf of our client to request a license for the following work:

WORK DETAILS:
- Title: ${work.title}
- Author: ${work.author}
- Work Type: ${work.workType}
- Territory: ${work.territory}
- Registration Number: ${work.registrationNumber || 'Not available'}

LICENSE REQUEST:
- License Type: ${payload.licenseType}
- Intended Use: ${payload.intendedUse}
- Territory: ${payload.territory}
- Duration: ${payload.duration || 'Perpetual'}
- Commercial Use: ${payload.commercialUse ? 'Yes' : 'No'}
- Estimated Audience: ${payload.estimatedAudience || 'Not specified'}
- Distribution Channels: ${payload.distributionChannels?.join(', ') || 'Not specified'}

CLIENT INFORMATION:
- Organization: ${payload.contactInfo.organization}
- Contact Person: ${payload.contactInfo.contactPerson}
- Email: ${payload.contactInfo.email}
- Phone: ${payload.contactInfo.phone || 'Not provided'}
- Address: ${payload.contactInfo.address || 'Not provided'}

${payload.budget ? `BUDGET RANGE: ${payload.budget.min} - ${payload.budget.max} ${payload.budget.currency}` : ''}

${payload.additionalInfo ? `ADDITIONAL INFORMATION:\n${payload.additionalInfo}` : ''}

Please provide us with:
1. Confirmation of rights ownership for this work
2. Available license terms and conditions
3. Applicable fees and payment terms
4. Required documentation
5. Estimated processing timeline

We appreciate your prompt attention to this request.

Best regards,
Vietrro Legal Team
legal@vietrro.com
+84 (0) 28 xxxx xxxx

---
This is an automated request generated by Vietrro Platform.
Request ID: ${Date.now()}
Generated on: ${new Date().toISOString()}
    `.trim();
  }

  /**
   * Send email to CMO
   */
  private async sendCMOEmail(
    to: string,
    content: string,
    payload: CMORequestPayload
  ): Promise<void> {
    try {
      const subject = `${payload.urgentRequest ? '[URGENT] ' : ''}License Request - Vietrro Platform`;

      const emailData = {
        recipient: to,
        subject,
        content,
        relatedEntity: 'pre-license-request',
        logStatus: 'sent',
        sentAt: new Date().toISOString()
      };

      const result = await apiClient.createRecord('email-log', emailData);

      if (!result.success) {
        throw result.error;
      }

      console.log(`📧 Email sent to CMO: ${to}`);
    } catch (error) {
      console.error('❌ Failed to send CMO email:', error);
      throw new Error('Không thể gửi email đến CMO');
    }
  }

  /**
   * Log CMO request for tracking
   */
  private async logCMORequest(
    cmo: CMOOrganization,
    payload: CMORequestPayload,
    response: CMORequestResponse
  ): Promise<void> {
    try {
      const logData = {
        cmoOrganization: cmo.documentId,
        workId: payload.workId,
        requestId: response.requestId,
        cmoReferenceNumber: response.cmoReferenceNumber,
        status: response.status,
        submissionMethod: cmo.hasAPI ? 'api' : 'email',
        payload: {
          licenseType: payload.licenseType,
          intendedUse: payload.intendedUse,
          territory: payload.territory,
          commercialUse: payload.commercialUse
        },
        estimatedResponseTime: response.estimatedResponseTime,
        
      };

      await apiClient.createRecord('cmo-request-log', logData);
      console.log(`📋 CMO request logged: ${response.requestId}`);
    } catch (error) {
      console.warn('⚠️ Failed to log CMO request:', error);
      // Don't throw error as main operation was successful
    }
  }

  /**
   * Generate request number
   */
  private generateRequestNumber(): string {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const timestamp = Date.now().toString().slice(-6);

    return `VTR-PLR-${year}${month}${day}-${timestamp}`;
  }

  /**
   * Get fallback CMOs if API fails
   */
  private getFallbackCMOs(): CMOOrganization[] {
    return [
      {
        id: 'ascap',
        name: 'ASCAP (American Society of Composers, Authors and Publishers)',
        code: 'ASCAP',
        country: 'US',
        website: 'https://www.ascap.com',
        contactEmail: 'licensing@ascap.com',
        contactPhone: '+1-212-621-6000',
        supportedWorkTypes: ['music', 'all'],
        processingTimeEstimate: 14,
        averageFeeRange: { min: 100, max: 5000, currency: 'USD' },
        isActive: true,
        hasAPI: false,
        requiresManualSubmission: true,
        integrationNotes: 'Email-based submissions only'
      },
      {
        id: 'bmi',
        name: 'BMI (Broadcast Music, Inc.)',
        code: 'BMI',
        country: 'US',
        website: 'https://www.bmi.com',
        contactEmail: 'licensing@bmi.com',
        contactPhone: '+1-212-586-2000',
        supportedWorkTypes: ['music', 'all'],
        processingTimeEstimate: 14,
        averageFeeRange: { min: 100, max: 5000, currency: 'USD' },
        isActive: true,
        hasAPI: false,
        requiresManualSubmission: true,
        integrationNotes: 'Email-based submissions only'
      },
      {
        id: 'gema',
        name: 'GEMA (Gesellschaft für musikalische Aufführungs- und mechanische Vervielfältigungsrechte)',
        code: 'GEMA',
        country: 'DE',
        website: 'https://www.gema.de',
        contactEmail: 'licensing@gema.de',
        contactPhone: '+49-30-21245-00',
        supportedWorkTypes: ['music', 'all'],
        processingTimeEstimate: 21,
        averageFeeRange: { min: 50, max: 3000, currency: 'EUR' },
        isActive: true,
        hasAPI: false,
        requiresManualSubmission: true,
        integrationNotes: 'German language preferred for communications'
      },
      {
        id: 'socan',
        name: 'SOCAN (Society of Composers, Authors and Music Publishers of Canada)',
        code: 'SOCAN',
        country: 'CA',
        website: 'https://www.socan.com',
        contactEmail: 'licensing@socan.com',
        contactPhone: '+1-416-445-8700',
        supportedWorkTypes: ['music', 'all'],
        processingTimeEstimate: 14,
        averageFeeRange: { min: 75, max: 4000, currency: 'CAD' },
        isActive: true,
        hasAPI: false,
        requiresManualSubmission: true,
        integrationNotes: 'Bilingual support (English/French)'
      },
      {
        id: 'sesac',
        name: 'SESAC (Society of European Stage Authors and Composers)',
        code: 'SESAC',
        country: 'US',
        website: 'https://www.sesac.com',
        contactEmail: 'licensing@sesac.com',
        contactPhone: '+1-615-320-0055',
        supportedWorkTypes: ['music', 'all'],
        processingTimeEstimate: 10,
        averageFeeRange: { min: 150, max: 6000, currency: 'USD' },
        isActive: true,
        hasAPI: false,
        requiresManualSubmission: true,
        integrationNotes: 'Smaller catalog but faster processing'
      },
      {
        id: 'vcpmc',
        name: 'VCPMC (Vietnam Center for Protection of Music Copyright)',
        code: 'VCPMC',
        country: 'VN',
        website: 'https://www.vcpmc.org.vn',
        contactEmail: 'info@vcpmc.org.vn',
        contactPhone: '+84-24-3934-7120',
        supportedWorkTypes: ['music', 'all'],
        processingTimeEstimate: 30,
        averageFeeRange: { min: 500000, max: 10000000, currency: 'VND' },
        isActive: true,
        hasAPI: false,
        requiresManualSubmission: true,
        integrationNotes: 'Vietnamese language required, local presence preferred'
      }
    ];
  }

  // ========================================
  // BATCH OPERATIONS & ANALYTICS
  // ========================================

  /**
   * Batch verify external works
   */
  async batchVerifyExternalWorks(
    workIds: string[],
    verificationNotes?: string
  ): Promise<{ successful: number; failed: number; results: any[] }> {
    try {
      console.log(`🔄 Batch verifying ${workIds.length} external works`);

      const results = [];
      let successful = 0;
      let failed = 0;

      // Process in chunks
      const chunkSize = 5;
      for (let i = 0; i < workIds.length; i += chunkSize) {
        const chunk = workIds.slice(i, i + chunkSize);

        const chunkPromises = chunk.map(async (workId) => {
          try {
            const result = await this.verifyExternalWork(workId, verificationNotes);
            successful++;
            return { workId, success: true, data: result };
          } catch (error) {
            failed++;
            return { workId, success: false, error: error.message };
          }
        });

        const chunkResults = await Promise.all(chunkPromises);
        results.push(...chunkResults);

        // Small delay between chunks
        if (i + chunkSize < workIds.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      console.log(`✅ Batch verification completed: ${successful}/${workIds.length} successful`);

      return {
        successful,
        failed,
        results
      };
    } catch (error) {
      console.error('❌ Batch verification failed:', error);
      throw new Error('Không thể xác minh hàng loạt external works. Vui lòng thử lại sau.');
    }
  }

  /**
   * Get CMO request statistics
   */
  async getCMORequestStats(period?: { from: string; to: string }): Promise<{
    totalRequests: number;
    successfulRequests: number;
    pendingRequests: number;
    averageResponseTime: number;
    topCMOs: Array<{ cmo: string; count: number }>;
    responseTimeByMethod: { api: number; email: number };
  }> {
    try {
      console.log('📊 Generating CMO request statistics');

      const filters: Record<string, any> = {};

      if (period) {
        filters['createdAt[$gte]'] = period.from;
        filters['createdAt[$lte]'] = period.to;
      }

      const result = await apiClient.searchRecords(
        'cmo-request-log',
        filters,
        { page: 1, pageSize: 1000 },
        { populate: 'cmoOrganization' }
      );

      const logs = result.data?.data || [];

      // Calculate statistics
      const totalRequests = logs.length;
      const successfulRequests = logs.filter(log => log.status === 'completed').length;
      const pendingRequests = logs.filter(log => ['submitted', 'pending', 'processing'].includes(log.status)).length;

      // Average response time (mock calculation)
      const avgResponseTime = logs.length > 0
        ? logs.reduce((sum, log) => sum + (log.estimatedResponseTime || 14), 0) / logs.length
        : 0;

      // Top CMOs
      const cmoCount = logs.reduce((acc, log) => {
        const cmoName = log.cmoOrganization?.name || 'Unknown';
        acc[cmoName] = (acc[cmoName] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const topCMOs = Object.entries(cmoCount)
        .map(([cmo, count]) => ({ cmo, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Response time by method
      const apiRequests = logs.filter(log => log.submissionMethod === 'api');
      const emailRequests = logs.filter(log => log.submissionMethod === 'email');

      const responseTimeByMethod = {
        api: apiRequests.length > 0
          ? apiRequests.reduce((sum, log) => sum + (log.estimatedResponseTime || 0), 0) / apiRequests.length
          : 0,
        email: emailRequests.length > 0
          ? emailRequests.reduce((sum, log) => sum + (log.estimatedResponseTime || 0), 0) / emailRequests.length
          : 0
      };

      const stats = {
        totalRequests,
        successfulRequests,
        pendingRequests,
        averageResponseTime: Math.round(avgResponseTime * 10) / 10,
        topCMOs,
        responseTimeByMethod
      };

      console.log('✅ CMO request statistics generated');
      return stats;
    } catch (error) {
      console.error('❌ Failed to generate CMO request statistics:', error);
      return {
        totalRequests: 0,
        successfulRequests: 0,
        pendingRequests: 0,
        averageResponseTime: 0,
        topCMOs: [],
        responseTimeByMethod: { api: 0, email: 0 }
      };
    }
  }

  /**
   * Sync with external CMO databases
   */
  async syncExternalCMODatabases(): Promise<{
    synced: number;
    updated: number;
    failed: number;
    lastSync: string;
  }> {
    try {
      console.log('🔄 Starting external CMO database sync');

      let synced = 0;
      let updated = 0;
      let failed = 0;

      // Mock sync process - in real implementation, call actual CMO APIs
      const cmos = await this.getAllCMOs();

      for (const cmo of cmos) {
        try {
          if (cmo.hasAPI && cmo.apiEndpoint) {
            // Simulate API sync
            console.log(`📡 Syncing with ${cmo.name}...`);
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Update last sync time
            await apiClient.updateRecordById('cmo-organization', cmo.documentId!, {
              lastSyncAt: new Date().toISOString()
            });

            synced++;
          } else {
            console.log(`⏩ Skipping ${cmo.name} (no API)`);
          }
        } catch (error) {
          console.error(`❌ Sync failed for ${cmo.name}:`, error);
          failed++;
        }
      }

      const result = {
        synced,
        updated,
        failed,
        lastSync: new Date().toISOString()
      };

      console.log(`✅ CMO database sync completed: ${synced} synced, ${failed} failed`);
      return result;
    } catch (error) {
      console.error('❌ CMO database sync failed:', error);
      throw new Error('Không thể đồng bộ database CMO. Vui lòng thử lại sau.');
    }
  }

  // ========================================
  // UTILITY FORMATTERS
  // ========================================

  /**
   * Format currency based on CMO's currency
   */
  private formatCurrency(amount: number, currency: string): string {
    const locale = currency === 'VND' ? 'vi-VN' :
      currency === 'EUR' ? 'de-DE' :
        currency === 'CAD' ? 'en-CA' : 'en-US';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(amount);
  }

  /**
   * Format processing time
   */
  private formatProcessingTime(days: number): string {
    if (days === 1) return '1 ngày';
    if (days < 7) return `${days} ngày`;
    if (days < 30) return `${Math.round(days / 7)} tuần`;
    return `${Math.round(days / 30)} tháng`;
  }

  /**
   * Validate work data
   */
  private validateWorkData(workData: Partial<ExternalWork>): void {
    if (!workData.title || workData.title.trim().length < 2) {
      throw new Error('Tiêu đề work không hợp lệ');
    }

    if (!workData.author || workData.author.trim().length < 2) {
      throw new Error('Tên tác giả không hợp lệ');
    }

    if (!workData.workType) {
      throw new Error('Loại work không được để trống');
    }

    const validWorkTypes = ['music', 'video', 'image', 'text', 'software', 'other'];
    if (!validWorkTypes.includes(workData.workType)) {
      throw new Error('Loại work không được hỗ trợ');
    }

    if (!workData.territory || workData.territory.length !== 2) {
      throw new Error('Territory phải là mã quốc gia 2 ký tự');
    }
  }

  /**
   * Validate CMO request payload
   */
  private validateCMORequestPayload(payload: CMORequestPayload): void {
    if (!payload.workId) {
      throw new Error('Work ID không được để trống');
    }

    if (!payload.licenseType) {
      throw new Error('Loại license không được để trống');
    }

    if (!payload.intendedUse || payload.intendedUse.length < 10) {
      throw new Error('Mô tả mục đích sử dụng phải có ít nhất 10 ký tự');
    }

    if (!payload.territory || payload.territory.length !== 2) {
      throw new Error('Territory phải là mã quốc gia 2 ký tự');
    }

    if (!payload.contactInfo.organization) {
      throw new Error('Tên tổ chức không được để trống');
    }

    if (!payload.contactInfo.contactPerson) {
      throw new Error('Tên người liên hệ không được để trống');
    }

    if (!payload.contactInfo.email || !this.isValidEmail(payload.contactInfo.email)) {
      throw new Error('Email không hợp lệ');
    }
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

// ========================================
// SINGLETON EXPORT
// ========================================

export const cmoService = new CMOService();
export default cmoService;
